import { useEffect, useState } from 'react'
import { Card, CardContent } from '@/components/ui'
import { MessageSquare, Bot, User, ChevronDown, ChevronUp, TrendingUp, Users, Target, BarChart3 } from 'lucide-react'
import { fetchConversations, fetchChatAnalytics } from '@/services/chat.service'

interface Conversation {
  id: string
  session_id: string
  messages: { role: string; content: string }[]
  message_count: number
  valuation_completed: boolean
  lead_id: string | null
  created_at: string
  updated_at: string
}

interface Analytics {
  totalConversations: number
  totalToday: number
  totalWeek: number
  valuationCompleted: number
  leadsFromChat: number
}

export function ConversazioniPanel() {
  const [conversations, setConversations] = useState<Conversation[]>([])
  const [analytics, setAnalytics] = useState<Analytics | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [expandedId, setExpandedId] = useState<string | null>(null)

  useEffect(() => {
    loadData()
  }, [])

  const loadData = async () => {
    setIsLoading(true)
    try {
      const [convos, stats] = await Promise.all([
        fetchConversations(50),
        fetchChatAnalytics(),
      ])
      setConversations(convos)
      setAnalytics(stats)
    } catch (err) {
      console.error('Errore caricamento conversazioni:', err)
    } finally {
      setIsLoading(false)
    }
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('it-IT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  const conversionRate = analytics && analytics.totalConversations > 0
    ? ((analytics.valuationCompleted / analytics.totalConversations) * 100).toFixed(0)
    : '0'

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6">
      {/* Analytics Cards */}
      {analytics && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
          <Card className="border-0 shadow-md">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-medium text-gray-500">Totale Chat</p>
                  <p className="text-2xl font-bold text-gray-900">{analytics.totalConversations}</p>
                </div>
                <div className="bg-blue-100 p-2 rounded-full">
                  <MessageSquare className="h-5 w-5 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-md">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-medium text-gray-500">Oggi</p>
                  <p className="text-2xl font-bold text-gray-900">{analytics.totalToday}</p>
                </div>
                <div className="bg-green-100 p-2 rounded-full">
                  <BarChart3 className="h-5 w-5 text-green-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-md">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-medium text-gray-500">Questa Settimana</p>
                  <p className="text-2xl font-bold text-gray-900">{analytics.totalWeek}</p>
                </div>
                <div className="bg-purple-100 p-2 rounded-full">
                  <TrendingUp className="h-5 w-5 text-purple-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-md">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-medium text-gray-500">Con Valutazione</p>
                  <p className="text-2xl font-bold text-emerald-600">{conversionRate}%</p>
                </div>
                <div className="bg-emerald-100 p-2 rounded-full">
                  <Target className="h-5 w-5 text-emerald-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-md">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-medium text-gray-500">Lead da Chat</p>
                  <p className="text-2xl font-bold text-orange-600">{analytics.leadsFromChat}</p>
                </div>
                <div className="bg-orange-100 p-2 rounded-full">
                  <Users className="h-5 w-5 text-orange-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Conversations List */}
      <div>
        <h2 className="text-lg font-semibold text-gray-900 mb-4">
          Ultime conversazioni
        </h2>

        {isLoading ? (
          <div className="text-center py-12 text-gray-500">Caricamento...</div>
        ) : conversations.length === 0 ? (
          <Card className="border-0 shadow-md">
            <CardContent className="p-12 text-center">
              <div className="bg-gray-100 w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-3">
                <MessageSquare className="h-7 w-7 text-gray-400" />
              </div>
              <h3 className="text-base font-semibold text-gray-900 mb-1">Nessuna conversazione</h3>
              <p className="text-sm text-gray-500">Le conversazioni dell'assistente AI appariranno qui.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {conversations.map((conv) => (
              <Card key={conv.id} className="border-0 shadow-md overflow-hidden">
                <button
                  onClick={() => setExpandedId(expandedId === conv.id ? null : conv.id)}
                  className="w-full p-4 flex items-center justify-between hover:bg-gray-50 transition-colors text-left"
                >
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-full ${conv.valuation_completed ? 'bg-emerald-100' : 'bg-gray-100'}`}>
                      <MessageSquare className={`h-4 w-4 ${conv.valuation_completed ? 'text-emerald-600' : 'text-gray-500'}`} />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-900">
                        {formatDate(conv.created_at)}
                        {conv.lead_id && (
                          <span className="ml-2 px-1.5 py-0.5 text-[10px] bg-orange-100 text-orange-700 rounded">Lead</span>
                        )}
                        {conv.valuation_completed && (
                          <span className="ml-1 px-1.5 py-0.5 text-[10px] bg-emerald-100 text-emerald-700 rounded">Valutazione</span>
                        )}
                      </p>
                      <p className="text-xs text-gray-500">
                        {conv.message_count} messaggi
                      </p>
                    </div>
                  </div>
                  {expandedId === conv.id ? (
                    <ChevronUp className="h-4 w-4 text-gray-400" />
                  ) : (
                    <ChevronDown className="h-4 w-4 text-gray-400" />
                  )}
                </button>

                {expandedId === conv.id && (
                  <div className="border-t border-gray-200 p-4 bg-gray-50 space-y-3 max-h-96 overflow-y-auto">
                    {conv.messages.map((msg, i) => (
                      <div key={i} className={`flex gap-2 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                        {msg.role === 'assistant' && (
                          <div className="bg-primary-100 p-1 rounded-full h-6 w-6 flex items-center justify-center shrink-0 mt-1">
                            <Bot className="h-3 w-3 text-primary-600" />
                          </div>
                        )}
                        <div className={`max-w-[80%] px-3 py-2 rounded-xl text-xs leading-relaxed ${
                          msg.role === 'user'
                            ? 'bg-primary-600 text-white'
                            : 'bg-white text-gray-700 border border-gray-200'
                        }`}>
                          {msg.content}
                        </div>
                        {msg.role === 'user' && (
                          <div className="bg-gray-200 p-1 rounded-full h-6 w-6 flex items-center justify-center shrink-0 mt-1">
                            <User className="h-3 w-3 text-gray-600" />
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
